import client from "./client";

export type SocialDownloadQuality = "best" | "1080" | "720" | "480" | "360";
export type SocialDownloadStatus = "queued" | "processing" | "done" | "error";

export interface SocialDownloadResult {
  source: string;
  file_name: string;
  file_url: string;
  file_size_bytes?: number;
  expires_at?: string | null;
}

export interface SocialDownloadJob {
  id: string;
  source: "youtube" | "instagram";
  quality: SocialDownloadQuality;
  status: SocialDownloadStatus;
  attempts?: number;
  result?: SocialDownloadResult | null;
  result_code?: string | null;
  finished_at?: string | null;
}

export interface SocialDownloadResponse {
  success: boolean;
  message?: string;
  job?: SocialDownloadJob;
  execution_enabled?: boolean;
}

export interface SocialDownloadPollOptions {
  signal?: AbortSignal;
  idempotencyKey?: string;
  pollDelaysMs?: readonly number[];
  onStatus?: (job: SocialDownloadJob, pollNumber: number) => void;
}

export type SocialDownloadProvisionResult =
  | { state: "ready"; result: SocialDownloadResult; job: SocialDownloadJob }
  | { state: "pending"; job: SocialDownloadJob; executionEnabled: boolean };

const DEFAULT_POLL_DELAYS_MS = [1_000, 2_000, 4_000, 6_000, 8_000] as const;

export async function validateSocialDownloadUrl(
  url: string,
  signal?: AbortSignal
): Promise<{ success: boolean; source: "youtube" | "instagram"; qualities: SocialDownloadQuality[] }> {
  const { data } = await client.post("/media/social-download/validate.php", { url }, { signal });
  return data;
}

export async function submitSocialDownload(
  url: string,
  quality: SocialDownloadQuality,
  idempotencyKey: string,
  signal?: AbortSignal
): Promise<SocialDownloadResponse> {
  const { data } = await client.post<SocialDownloadResponse>(
    "/media/social-download/submit.php",
    { url, quality },
    { signal, headers: { "Idempotency-Key": idempotencyKey } }
  );
  return data;
}

export async function getSocialDownloadStatus(
  id: string,
  signal?: AbortSignal
): Promise<SocialDownloadResponse> {
  const { data } = await client.get<SocialDownloadResponse>(
    `/media/social-download/status.php?id=${encodeURIComponent(id)}`,
    { signal }
  );
  return data;
}

export async function submitAndPollSocialDownload(
  url: string,
  quality: SocialDownloadQuality,
  options: SocialDownloadPollOptions = {}
): Promise<SocialDownloadProvisionResult> {
  const idempotencyKey = options.idempotencyKey ?? createSocialDownloadIdempotencyKey();
  const delays = options.pollDelaysMs ?? DEFAULT_POLL_DELAYS_MS;
  const submitted = await submitSocialDownload(url, quality, idempotencyKey, options.signal);
  if (!submitted.success || !submitted.job) {
    throw new Error(submitted.message || "Social download job was not created");
  }

  let response = submitted;
  let job = submitted.job;
  for (let pollNumber = 0; ; pollNumber += 1) {
    options.onStatus?.(job, pollNumber);
    if (job.status === "done") {
      if (job.result && isSafeSocialDownloadUrl(job.result.file_url)) {
        return { state: "ready", result: job.result, job };
      }
      throw new Error("Download finished without a safe result");
    }
    if (job.status === "error") {
      throw new Error(response.message || "Download preparation failed");
    }
    if (pollNumber >= delays.length) {
      return { state: "pending", job, executionEnabled: response.execution_enabled === true };
    }

    await abortableDelay(delays[pollNumber], options.signal);
    response = await getSocialDownloadStatus(job.id, options.signal);
    if (!response.success || !response.job) {
      throw new Error(response.message || "Download status is unavailable");
    }
    job = response.job;
  }
}

export function isSafeSocialDownloadUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

function createSocialDownloadIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") {
    return `social-download-${cryptoApi.randomUUID()}`;
  }
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return `social-download-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, Math.max(0, milliseconds));
    const onAbort = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      reject(createAbortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function createAbortError(): DOMException {
  return new DOMException("Social download request cancelled", "AbortError");
}
